import { Formik } from "formik";
import { toast } from "react-toastify";
import * as Yup from "yup";
import { format } from "date-fns";
import { Input, Label } from "../../components/shared/InputField";
import { Select } from "../../components/shared/Select";
import {
  ButtonPrimary,
  ButtonPrimary1,
  Error,
  HStack,
  VStack,
} from "../../components/utils";
import {
  useCreatePoolTransaction,
  useUpdatePoolTransaction,
} from "../../queries/esopPlan";
import { useError } from "../../store/errorStore";
import {
  PoolTransaction,
  PoolTransactionCreateDto,
  PoolTransactionType,
} from "../../types/Grant";
import { formatWithTimeZone } from "../../utils/date";

type Props = {
  mode?: "Add" | "Edit";
  transaction?: PoolTransaction;
  isFirstTransaction?: boolean;
  onClose: () => void;
};

const validationSchema = Yup.object().shape({
  transactionDate: Yup.date().required("Transaction date is required"),
  transactionType: Yup.string().required("Transaction type is required"),
  noOfOptions: Yup.number()
    .required("Number of options is required")
    .integer("Options must be a whole number")
    .moreThan(0, "Options must be greater than 0"),
  notes: Yup.string(),
});

export function AddOrEditPoolTransaction({
  mode = "Add",
  transaction,
  isFirstTransaction = false,
  onClose,
}: Readonly<Props>) {
  const { mutate: createTransaction, isLoading: creating } =
    useCreatePoolTransaction();
  const { mutate: updateTransaction, isLoading: updating } =
    useUpdatePoolTransaction();
  const errorMessage = useError();

  const defaultType =
    mode === "Edit"
      ? transaction?.transactionType ?? PoolTransactionType.Amended
      : isFirstTransaction
      ? PoolTransactionType.Created
      : PoolTransactionType.Amended;

  const initialValues = {
    transactionDate:
      mode === "Edit" && transaction
        ? format(new Date(transaction.transactionDate), "yyyy-MM-dd")
        : format(new Date(), "yyyy-MM-dd"),
    transactionType: defaultType,
    noOfOptions: mode === "Edit" && transaction ? transaction.noOfOptions : 0,
    notes: mode === "Edit" && transaction ? transaction.notes ?? "" : "",
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={(values, { resetForm }) => {
        if (mode === "Edit" && transaction) {
          updateTransaction(
            {
              id: transaction.id,
              transactionDate: formatWithTimeZone(values.transactionDate),
              transactionType: values.transactionType as PoolTransactionType,
              noOfOptions: values.noOfOptions,
              notes: values.notes || undefined,
            },
            {
              onSuccess: () => {
                resetForm();
                toast("Transaction updated successfully", { type: "success" });
                onClose();
              },
              onError: (err: any) => {
                const msg =
                  err.response?.data?.reason ||
                  err.response?.data?.errorRaw ||
                  "Failed to update transaction";
                errorMessage.setMessage(msg);
                toast(msg, { type: "error", autoClose: 2000 });
              },
            }
          );
        } else {
          const dto: PoolTransactionCreateDto = {
            transactionDate: formatWithTimeZone(values.transactionDate),
            transactionType: values.transactionType as PoolTransactionType,
            noOfOptions: values.noOfOptions,
            notes: values.notes || undefined,
          };
          createTransaction(dto, {
            onSuccess: () => {
              resetForm();
              toast("Transaction added successfully", { type: "success" });
              onClose();
            },
            onError: (err: any) => {
              const msg =
                err.response?.data?.reason ||
                err.response?.data?.errorRaw ||
                "Failed to add transaction";
              errorMessage.setMessage(msg);
              toast(msg, { type: "error", autoClose: 2000 });
            },
          });
        }
      }}
    >
      {({ touched, errors, getFieldProps, isValid, handleSubmit }) => (
        <VStack className="w-full gap-6">
          <HStack className="gap-8">
            <div className="flex-1">
              <Label className="required text-sm font-normal">
                Transaction Date
              </Label>
              <Input type="date" {...getFieldProps("transactionDate")} />
              {touched.transactionDate && errors.transactionDate && (
                <Error text={errors.transactionDate as string} />
              )}
            </div>
            <div className="flex-1">
              <Label className="required text-sm font-normal">
                Transaction Type
              </Label>
              <Select
                options={[
                  PoolTransactionType.Created,
                  PoolTransactionType.Amended,
                ].map((type) => ({ value: type.toUpperCase(), label: type }))}
                textGetter={(option) => option.label}
                valueGetter={(option) => option.value}
                isPlaceHolderDisabled
                {...getFieldProps("transactionType")}
                value={getFieldProps("transactionType").value.toUpperCase()}
              />
              {touched.transactionType && errors.transactionType && (
                <Error text={errors.transactionType} />
              )}
            </div>
          </HStack>
          <div>
            <Label className="required text-sm font-normal">
              Number of Options
            </Label>
            <Input
              type="number"
              min={1}
              step={1}
              {...getFieldProps("noOfOptions")}
              className="w-56"
            />
            {touched.noOfOptions && errors.noOfOptions && (
              <Error text={errors.noOfOptions as string} />
            )}
          </div>
          <div>
            <Label className="text-sm font-normal">Notes (optional)</Label>
            <textarea
              className="w-full px-3 py-2 border border-borderColor rounded text-sm focus:outline-none focus:ring-1 focus:ring-primary resize-none"
              rows={3}
              {...getFieldProps("notes")}
            />
          </div>
          <HStack className="justify-between mt-2">
            <ButtonPrimary1 onClick={onClose}>Back</ButtonPrimary1>
            <ButtonPrimary
              loading={creating || updating}
              className={`${!isValid && "bg-gray-400"}`}
              onClick={() => handleSubmit()}
            >
              {mode === "Edit" ? "Update Transaction" : "Add Transaction"}
            </ButtonPrimary>
          </HStack>
        </VStack>
      )}
    </Formik>
  );
}

export function AddOrEditPoolTransactionWrapper(props: Readonly<Props>) {
  return (
    <div>
      <div className="px-10 text-lg font-medium border-b py-7">
        <h6 className="flex justify-between">
          {props.mode === "Edit" ? "Edit Transaction" : "Add Transaction"}
          <span onClick={props.onClose} className="cursor-pointer">
            X
          </span>
        </h6>
      </div>
      <div className="px-10 pt-4 pb-8">
        <AddOrEditPoolTransaction {...props} />
      </div>
    </div>
  );
}
